/* eslint-disable ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
// Ember Imports
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// Internal Imports
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import { cleanFileName } from '@linc-technologies/linced-common/utils/file-names';
import { naturalSort } from '@linc-technologies/linced-common/utils/sorting';
import { getTableExportData, parseCell } from 'linced-app/utils/table';
import moment from 'moment/moment';
import { action, computed } from '@ember/object';
import { compact, findBy } from '@linc-technologies/linced-common/utils/arrays';

const SCHOOL_LISTS = [
  { key: 'daily-roll', label: 'Daily Roll' },
  { key: 'weekly-roll', label: 'Weekly Roll' },
  {
    key: 'perm-opts-paid',
    label: 'Permissions and Options Report (including Paid column)',
  },
  {
    key: 'perm-opts-unpaid',
    label: 'Permissions and Options Report (excluding Paid)',
  },
  { key: 'generic-blank', label: 'Generic List with Blank Columns' },
  { key: 'staff-generic', label: 'Staff Generic List' },
];

const CLASS_LISTS = [
  { key: 'daily-roll', label: 'Daily Roll' },
  { key: 'weekly-roll', label: 'Weekly Roll' },
  {
    key: 'perm-opts-paid',
    label: 'Permissions and Options Report (including Paid column)',
  },
  {
    key: 'perm-opts-unpaid',
    label: 'Permissions and Options Report (excluding Paid)',
  },
  { key: 'generic-blank', label: 'Generic List with Blank Columns' },
];

export default class PermissionReplyDownloads extends Component {
  // Services
  @service
  csv;

  @service
  dates;

  @service
  store;

  @service
  strings;

  @service
  toaster;

  EnrolmentStatus = EnrolmentStatus;
  SCHOOL_LISTS = SCHOOL_LISTS;
  CLASS_LISTS = CLASS_LISTS;
  event = null;
  learners = null;

  @tracked
  homerooms = null;

  groupMembers = null;

  @computed('homerooms.@each.name', 'learners.[]')
  get homeroomLearners() {
    if (isEmpty(this.homerooms) || isEmpty(this.learners)) {
      return [];
    }

    return this.homerooms
      .slice()
      .sort(naturalSort('name'))
      .map((homeroom) => {
        return {
          homeroom,
          learners: compact(
            homeroom.currentLearners.map((learner) => {
              return findBy(this.learners, 'id', learner.personId);
            }),
          ),
        };
      })
      .filter((data) => {
        return !isEmpty(data.learners);
      });
  }

  @tracked
  dialogOpen = false;

  @tracked
  downloading = false;

  @tracked
  schoolBlankColumn = 1;

  @action
  setSchoolBlankColumn(value) {
    this.schoolBlankColumn = value;
  }

  @tracked
  schoolListSelection = [];

  @tracked
  schoolSort = 'lastName';

  @action
  setSchoolSort(value) {
    this.schoolSort = value;
  }

  @tracked
  classBlankColumn = 1;

  @action
  setClassBlankColumn(value) {
    this.classBlankColumn = value;
  }

  @tracked
  classListSelection = [];

  @tracked
  classSort = 'lastName';

  @action
  setClassSort(value) {
    this.classSort = value;
  }

  date = moment().format('YYYY-MM-DD');

  reset() {
    this.schoolListSelection = [];
    this.classListSelection = [];
    this.schoolBlankColumn = 1;
    this.schoolSort = 'lastName';
    this.classBlankColumn = 1;
    this.classSort = 'lastName';
    this.downloading = false;
  }

  closeDialog() {
    this.dialogOpen = false;
  }

  @action
  openDialog() {
    this.store
      .findAll('medical/medication')
      .then(() => {
        this.store
          .query('group', {
            year: moment().format('YYYY'),
            groupType: GroupType.HOMEROOM,
          })
          .then((groups) => {
            this.reset();
            this.dialogOpen = true;
            this.homerooms = groups;
          })
          .catch(() => {
            this.toaster.error('Error loading homerooms!');
          });
      })
      .catch(() => {
        this.toaster.error('Error loading medications!');
      });
  }

  @action
  addSchoolListSelection(list) {
    if (this.schoolListSelection.includes(list)) {
      this.schoolListSelection = this.schoolListSelection.filter(
        (l) => l !== list,
      );
    } else {
      this.schoolListSelection = [...this.schoolListSelection, list];
    }
  }

  @action
  addClassListSelection(list) {
    if (this.classListSelection.includes(list)) {
      this.classListSelection = this.classListSelection.filter(
        (l) => l !== list,
      );
    } else {
      this.classListSelection = [...this.classListSelection, list];
    }
  }

  @action
  async download() {
    try {
      this.downloading = true;

      // Collect rendered tables from hidden DOM and export to CSV.
      let wrappers = this.element.querySelectorAll(
        '.csv-downloads [data-file-label]',
      );
      let index = 0;
      wrappers.forEach((wrapper) => {
        let label = wrapper.getAttribute('data-file-label') || 'Export';
        let container = wrapper.querySelector('md-table-container');
        if (!container) {
          return;
        }
        if (!container.id) {
          container.id = `csv-export-${Date.now()}-${index++}`;
        }
        let data = getTableExportData(container.id, 'csv', parseCell);
        let rows = data && data.rows;
        if (isEmpty(rows)) {
          return;
        }
        let title = (this.event && this.event.title) || 'Export';
        let fileName = cleanFileName(`${title} - ${label}`) + '.csv';
        this.csv.export(rows, { fileName, withSeparator: false });
      });

      this.toaster.success('Downloads started');
      this.closeDialog();
    } catch (e) {
      console.error(e);
      this.toaster.error('Error generating CSV downloads');
    } finally {
      this.downloading = false;
    }
  }
}
